import React, { ComponentProps, KeyboardEvent, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { ContentState, Editor, EditorState, getDefaultKeyBinding } from 'draft-js';
import { ArrowElbowDownLeft, Sparkle } from 'phosphor-react';
import OutsideClickHandler from 'react-outside-click-handler';

import s from './index.module.css';

type Props = {
  loading?: boolean;
  anchorRef?: React.RefObject<Editor>;

  onSubmit: (text: string) => void;
  onCancel?: () => void;
  onBlur?: (e?: MouseEvent) => void;
  onFocus?: () => void;
} & Partial<ComponentProps<typeof Editor>>;

const initialState = EditorState.createWithContent(ContentState.createFromText(''));

export function AIPromptEditor(props: Props) {
  const { loading, onBlur, ...rest } = props;

  const [state, setState] = useState(initialState);
  const [focused, setFocused] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  const handleESC = () => {
    if (loading) {
      handleStop();
      return null;
    }
    onBlur?.();
    return null;
  };

  const handleSubmit = () => {
    const value = state.getCurrentContent().getPlainText();

    if (!value) {
      handleESC();
      return;
    }

    props.onSubmit(value);
  };

  const onKeyDown = (e: KeyboardEvent<{}>) => {
    if (e.key === 'Escape') {
      return handleESC();
    }
  };

  const handleStop = () => {
    props.onCancel?.();
  };

  useEffect(() => {
    if (loading) blockRef.current?.focus();
  }, [loading]);

  return (
    <OutsideClickHandler disabled={!onBlur || !focused || loading} onOutsideClick={() => onBlur?.()}>
      <div ref={blockRef} onKeyDown={onKeyDown} className={cx(s.block, { [s.loading]: loading })} tabIndex={0}>
        <Sparkle width="1.25em" height="1.25em" className={s.icon} />
        <div className={s.editor}>
          {loading ? (
            'AI is writing...'
          ) : (
            <Editor
              {...rest}
              ref={props.anchorRef}
              editorState={state}
              onChange={setState}
              placeholder="Ask Safari Portal AI to write something..."
              onFocus={() => {
                setFocused(true);
                props.onFocus?.();
              }}
              onBlur={() => {
                setFocused(false);
              }}
              keyBindingFn={(e) => {
                if (e.key === 'Escape') {
                  handleESC();
                  return null;
                }
                if (e.key === 'Enter') {
                  handleSubmit();
                  return null;
                }
                return getDefaultKeyBinding(e);
              }}
            />
          )}
        </div>
        <div className={s.hint}>
          {loading ? (
            <button className={s.hint} onClick={handleStop}>
              Stop <span className={s.hintSymbol}>ESC</span>
            </button>
          ) : (
            <>
              {onBlur && (
                <button className={s.hint} onClick={() => onBlur()}>
                  Cancel <span className={s.hintSymbol}>ESC</span>
                </button>
              )}
              <button className={s.hint} onClick={handleSubmit}>
                Done{' '}
                <span className={s.hintSymbol}>
                  <ArrowElbowDownLeft width={14} />
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </OutsideClickHandler>
  );
}
